/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import moment from "moment";
import { ReservationResponse } from "../../../../models/reservation/ReservationResponse";
import { Shift } from "../../../../models/calendar/Shift";
import CalendarDayReservationModal from "../../Reservation/CalendarDayReservationModal";
import { isCancellable } from "../../../../util/helperFunctions";
import ShiftModal from "../ShiftModal";
import UserProfilePicture from "../../../../components/Pictures/User/UserProfilePicture";
import ReservationStateIcons from "../../Reservation/ReservationStateIcons";
import { medicalRecordStatusTags } from "../../Day/CalendarListView";
import { PencilSquare } from "../../../../common/Icons/PencilSquare";
import Button from "../../../../components/Button";
import EmptyListText from "../../../../components/EmptyListText";
import { strings } from "../../../../common/Strings/Strings";
import { getValueOfActiveUser, UserRole } from "../../../../util/LocalStorageVariables";

interface Props {
  reload(): void;
  shift: Shift;
}

const ShiftListViewRow: React.FC<Props> = ({ reload, shift }: Props) => {
  const [selectedShiftData, setSelectedShiftData] = useState<Shift>();
  const [activeReservation, setActiveReservation] = useState<ReservationResponse>();
  const [openSchedule, setOpenSchedule] = useState<boolean>(false);
  const role: UserRole | null = getValueOfActiveUser("role");

  return (
    <div className="flex flex-row items-center" key={shift.id}>
      <div className="tw-card p-3" key={shift.id}>
        <div
          className="flex flex-row items-center cursor-pointer"
          onClick={() => setOpenSchedule((prev) => !prev)}
          role="button"
          tabIndex={0}
        >
          <div className="flex flex-row space-x-3">
            <div>
              {moment(shift.startDateTime).format("LT")} - {moment(shift.endDateTime).format("LT")}
            </div>
            <div className="w-6 h-6 hidden md:block">
              <UserProfilePicture userId={shift.collaborator.userId} />
            </div>
            <div>{shift.collaborator.fullName}</div>
          </div>
          <div className="flex flex-row space-x-1 ml-auto truncate">
            {shift.reservations?.map((reservation: ReservationResponse) => (
              <div
                className="rounded-full w-4 h-4 flex-shrink-0 border border-gray-300 dark:border-gray-700"
                style={{
                  backgroundColor: reservation.reservationType?.color || "#f37676",
                }}
              />
            ))}
          </div>
        </div>
        <div className={shift.reservations?.length || -1 > 0 ? "pt-3" : ""} hidden={!openSchedule}>
          {shift.reservations?.length === 0 ? (
            <div className="py-1">
              <EmptyListText text={strings.noReservationsFound} />
            </div>
          ) : (
            <></>
          )}
          {shift.reservations?.map((reservation: ReservationResponse) => (
            <div
              className="flex flex-row space-x-3 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-900"
              key={reservation.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setActiveReservation(reservation);
              }}
            >
              <div
                className="mt-1 rounded-full w-4 h-4 flex-shrink-0 border border-gray-300 dark:border-gray-700"
                style={{
                  backgroundColor: reservation.reservationType?.color || "#f37676",
                }}
              />
              <div className="flex flex-wrap flex-col md:flex-row md:items-center">
                <div className="pr-3">
                  {moment(reservation.startDateTime).format("LT")} - {moment(reservation.endDateTime).format("LT")}
                </div>
                <div className="flex flex-row flex-wrap">
                  <div className="font-bold pr-3">{reservation.pet?.name}</div>
                  <ReservationStateIcons
                    notArrivedVisible={false}
                    reservationState={reservation.state}
                    size="w-6 h-6 mr-3"
                  />
                </div>
                <div hidden={reservation.description?.length === 0} className="break-words pr-3">
                  {reservation.description}
                </div>
                <div>{medicalRecordStatusTags(reservation)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="ml-3">
        <Button
          variant="icon"
          onClick={() => {
            setSelectedShiftData(shift);
          }}
        >
          <span className="sr-only">Edit</span>
          <PencilSquare />
        </Button>
      </div>
      {activeReservation !== undefined ? (
        <CalendarDayReservationModal
          modalIsOpen={!!activeReservation}
          closeModal={() => {
            setActiveReservation(undefined);
          }}
          activeReservation={{
            ...activeReservation,
            cancellable: isCancellable(activeReservation, role || undefined),
          }}
          reload={() => {
            reload();
          }}
        />
      ) : (
        <></>
      )}
      {selectedShiftData ? (
        <ShiftModal
          selectedShiftData={selectedShiftData}
          onHide={() => {
            setSelectedShiftData(undefined);
          }}
          reload={reload}
          show={!!selectedShiftData}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default ShiftListViewRow;
